import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { FaUsers, FaExclamationTriangle, FaBars } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { FaLaptop, FaUserCog, FaSitemap, FaTimes } from 'react-icons/fa';
import { FaSpinner } from 'react-icons/fa';


export default function SessionManagement() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchSessions = async () => {
    try {
      // Use the full URL to your API endpoint
      const apiUrl = 'https://dev.sliitleo.org/Backend/getSessionData.php';
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        mode: 'cors',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      if (data && data.success) {
        setSessions(data.sessions || []);
      } else {
        const errorMsg = data?.error || 'Failed to load session data: Invalid response format';
        setError(errorMsg);
      }
    } catch (err) {
      //e.error('Fetch error:', err);
      setError(`Failed to load session data: ${err.message}. Please check your network connection and make sure the backend server is running.`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);
  // Get user info from localStorage
  const user = JSON.parse(localStorage.getItem('user')) || {
    name: 'Unknown User',
    role: 'Member',
    image: '/user.png',
  };

  if (loading) {
    return (
      <div className="flex bg-[#111827] text-white min-h-screen items-center justify-center px-4">
        <div className="bg-[#23272f] p-8 rounded-2xl border border-gray-700 shadow-lg">
          <div className="flex flex-col items-center gap-4">
            <div className="animate-spin text-4xl text-blue-500 mb-4">
              <FaSpinner />
            </div>
            <div className="text-xl font-semibold">Loading Sessions...</div>
            <div className="text-sm text-gray-400">Please wait while we fetch the session data</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex bg-[#111827] text-white min-h-screen items-center justify-center px-4">
        <div className="bg-[#23272f] p-8 rounded-2xl border border-gray-700 shadow-lg">
          <div className="flex flex-col items-center gap-4">
            <div className="text-4xl text-red-500 mb-4">
              <FaExclamationTriangle />
            </div>
            <div className="text-xl font-semibold text-red-500">Error Loading Sessions</div>
            <div className="text-sm text-gray-400">{error}</div>
            <button 
              onClick={() => {
                setError(null);
                setLoading(true);
                fetchSessions();
              }}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex bg-[#111827] text-white min-h-screen relative">
      {/* Mobile Sidebar Toggle */}
      <button 
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        className="fixed top-4 left-4 z-50 md:hidden bg-[#23272f] p-2 rounded-lg shadow-lg"
        aria-label="Toggle sidebar"
      >
        {isSidebarOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
      </button>

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform duration-300 ease-in-out z-40`}>
        <Sidebar onNavigate={() => setIsSidebarOpen(false)} />
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div className="w-full md:ml-64">
        <Topbar user={user} />
        <main className="mt-14 w-full px-4 py-6 md:px-8 md:py-10">
        {/* Header */}
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-4 sm:p-5 mb-6 sm:mb-8 flex items-center gap-3 sm:gap-4 shadow-lg">
          <span className="flex items-center gap-2 sm:gap-3">
            <span className="bg-indigo-600 p-2 rounded-full shadow-lg flex-shrink-0">
              <FaUsers className="text-white text-xl sm:text-2xl" />
            </span>
            <span className="text-base sm:text-lg font-bold tracking-wide text-white drop-shadow">
              Admin <span className="text-gray-400 font-normal">/ Session Management</span>
            </span>
          </span>
        </div>

        {/* Navigation Buttons */}
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-4 mb-8 w-full">
                  <Link to="/admin" className="bg-[#23272f] px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-white border border-gray-700 shadow hover:scale-105 transition w-full sm:w-auto">
                    <FaUserCog className="text-blue-400"/> Role Management
                  </Link>
                  <Link to="/session-management" className="bg-[#23272f] px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-white border border-gray-700 shadow hover:scale-105 transition w-full sm:w-auto">
                    <FaUsers className="text-indigo-400"/> Session Management
                  </Link>
                  <Link to="/device-management" className="bg-[#23272f] px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-white border border-gray-700 shadow hover:scale-105 transition w-full sm:w-auto">
                    <FaLaptop className="text-indigo-400"/> Device Management
                  </Link>
                  {/* <Link to="/path-management" className="bg-[#23272f] px-4 py-2 rounded-xl font-semibold flex items-center gap-2 text-white border border-gray-700 shadow hover:scale-105 transition w-full sm:w-auto">
                    <FaSitemap className="text-indigo-400"/> Role Path Management
                  </Link> */}
                </div>

        {/* Session Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sessions.map((session) => (
            <div key={session.id} className="bg-[#23272f] rounded-xl border border-gray-700 shadow hover:shadow-lg transition-shadow flex flex-col h-full">
              <div className="flex flex-col h-full p-4 sm:p-5">
                <div className="flex items-start gap-3 mb-3">
                  <span className="bg-gray-800 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg flex-shrink-0">
                    {session.name?.[0]?.toUpperCase() || '?'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-white text-base leading-tight truncate">
                      {session.name || 'Unknown User'}
                    </h3>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                      <span className="truncate">
                        {session.lastLogin || 'No login data'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="mt-auto pt-3 border-t border-gray-700">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {session.device && (
                      <span className="text-xs text-gray-400 bg-gray-800/50 px-2.5 py-1 rounded-full">
                        {session.device}
                      </span>
                    )}
                    <span className="text-xs text-gray-300 font-medium bg-blue-900/30 px-2.5 py-1 rounded-full ml-auto">
                      {session.logins || 0} {session.logins === 1 ? 'Login' : 'Logins'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        </main>
      </div>
    </div>
  );
}
